import { createFileRoute } from "@tanstack/react-router";
import ArticleBestPoolDeckMaterials from "@/pages/ArticleBestPoolDeckMaterials";
import { buildHead } from "@/lib/seo";
import { FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS } from "@/data/blogData";
import { FAQS_BEST_POOL_DECK_MATERIALS } from "@/data/bestPoolDeckMaterialsData";

const PATH = "/blog/best-pool-deck-materials-south-florida";
const CANONICAL = `https://precisionpaverspools.com${PATH}`;

// BlogPosting schema. The `image` field is included only once a verified
// featured-image URL has been supplied, so the structured data never
// references an empty value.
const blogPosting: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: "Best Pool Deck Materials for South Florida",
  description:
    "Compare pool deck materials for South Florida, including concrete pavers, travertine, marble, porcelain, and other options based on heat, traction, maintenance, drainage, design, and budget.",
  datePublished: "2026-10-13",
  dateModified: "2026-10-14",
  author: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
  },
  publisher: {
    "@type": "Organization",
    name: "Precision Pavers & Pool Design",
    url: "https://precisionpaverspools.com/",
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": CANONICAL,
  },
};

if (FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS) {
  blogPosting.image = FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS;
}

const jsonLd: Record<string, unknown>[] = [
  blogPosting,
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://precisionpaverspools.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://precisionpaverspools.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Best Pool Deck Materials for South Florida",
        item: CANONICAL,
      },
    ],
  },
];

// FAQPage schema is emitted only when visible FAQs exist, so the schema
// always exactly matches the on-page FAQ content. During the preparation
// phase the shared FAQ array is empty, so no FAQPage is emitted yet.
if (FAQS_BEST_POOL_DECK_MATERIALS.length > 0) {
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS_BEST_POOL_DECK_MATERIALS.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  });
}

export const Route = createFileRoute(
  "/blog/best-pool-deck-materials-south-florida",
)({
  head: () =>
    buildHead({
      title: "Best Pool Deck Materials for South Florida",
      description:
        "Compare pool deck materials for South Florida, including concrete pavers, travertine, marble, porcelain, and other options based on heat, traction, maintenance, drainage, design, and budget.",
      path: PATH,
      ogType: "article",
      ogImage: FEATURED_IMAGE_BEST_POOL_DECK_MATERIALS || undefined,
      jsonLd,
    }),
  component: ArticleBestPoolDeckMaterials,
});
